/**
 * Public admissions intake client (issue #9) — `POST /public/apply`,
 * `GET /public/programs`, and `GET /public/intake`.
 *
 * These routes use the shared public transport. The backend resolves the tenant
 * from the trusted Origin header and receives no session or X-Tenant claim.
 */
import { publicApi } from "@/lib/api/publicClient";

export interface PublicProgram {
  id: string;
  name: string;
}

/**
 * The detail fields a tenant collects (migration 054). The key set is closed:
 * an unrecognized key from the server is ignored rather than rendered, so the
 * form can never invent an input it has no presentation for.
 */
export const INTAKE_FIELD_KEYS = [
  "date_of_birth",
  "school_name",
  "grade_level",
  "major",
  "mother_name",
  "mother_phone",
  "mother_address",
  "father_name",
  "father_phone",
  "father_address",
] as const;

export type IntakeFieldKey = (typeof INTAKE_FIELD_KEYS)[number];

export interface IntakeField {
  key: IntakeFieldKey;
  required: boolean;
}

export interface ApplyRequest {
  name: string;
  email: string;
  phone?: string | null;
  program_id?: string | null;
  applicant_is: "parent" | "student";
  applicant_message?: string | null;
  submission_id?: string | null;
  turnstile_token?: string | null;
  date_of_birth?: string | null;
  school_name?: string | null;
  grade_level?: string | null;
  major?: string | null;
  mother_name?: string | null;
  mother_phone?: string | null;
  mother_address?: string | null;
  father_name?: string | null;
  father_phone?: string | null;
  father_address?: string | null;
  /**
   * A guardian's consent to collection of an under-13 applicant's data (D7).
   *
   * Deliberately absent from INTAKE_FIELD_KEYS. Those keys are the tenant's
   * configured set, each carrying an `enabled` flag the tenant owns, and a
   * consent gate a tenant can switch off is not a gate. The backend and
   * `lms_apply_v3` both read this outside the configured-field path.
   */
  guardian_consent?: boolean;
}

export interface ApplyResponse {
  id: string;
  confirmation_email_sent: boolean;
}

export function submitApplication(body: ApplyRequest): Promise<ApplyResponse> {
  return publicApi.post<ApplyResponse>("/public/apply", body);
}

export function getPublicPrograms(): Promise<PublicProgram[]> {
  return publicApi.get<PublicProgram[]>("/public/programs");
}

const KNOWN_KEYS = new Set<string>(INTAKE_FIELD_KEYS);

export async function getPublicIntakeFields(): Promise<IntakeField[]> {
  const response = await publicApi.get<{ fields?: IntakeField[] }>("/public/intake");
  return (response.fields ?? []).filter((field) => KNOWN_KEYS.has(field.key));
}
